/**
 * Drawer de adição de um vínculo (telefone, e-mail, endereço, relação, emprego…). O padrão do
 * cadastro: a tela de gestão mostra **só os dados** já vinculados; adicionar um novo acontece num
 * painel lateral, longe da lista. Assim o formulário nunca disputa espaço com o conteúdo, e —
 * detalhe técnico que importa — os campos de busca-com-cadastro-rápido (`ComboBuscaEntidade`) não
 * ficam aninhados em nenhum `<form>` da página.
 *
 * O componente é "burro" de propósito: os campos e a lógica de salvar vêm do painel via `children`
 * e `onSalvar`. `onSalvar` devolve `true` no sucesso — só então o drawer fecha. Editar campos
 * triviais (datas, observações) segue inline na própria linha; trocar a entidade vinculada, aqui.
 */
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet } from '@/components/ui/sheet';
import { Spinner } from '@/components/ui/misc';

export function DrawerAdicionar({
  titulo,
  descricao,
  rotuloAbrir = 'Adicionar',
  rotuloSalvar = 'Vincular',
  podeSalvar,
  salvando,
  onSalvar,
  onFechar,
  children,
}: {
  titulo: string;
  descricao?: string;
  rotuloAbrir?: string;
  rotuloSalvar?: string;
  podeSalvar: boolean;
  salvando: boolean;
  /** Executa o salvamento; devolve `true` no sucesso (o drawer fecha) ou `false` (permanece aberto). */
  onSalvar: () => Promise<boolean> | boolean;
  /** Chamado quando o drawer fecha — o painel reseta os campos aqui. */
  onFechar?: () => void;
  children: React.ReactNode;
}) {
  const [aberto, setAberto] = useState(false);

  function mudar(v: boolean) {
    setAberto(v);
    if (!v) onFechar?.();
  }

  async function salvar() {
    if (!podeSalvar || salvando) return;
    if (await onSalvar()) mudar(false);
  }

  return (
    <div className="flex justify-end border-t border-border pt-4">
      <Button variant="secundario" size="sm" onClick={() => setAberto(true)}>
        <Plus className="size-4" />
        {rotuloAbrir}
      </Button>
      <Sheet
        aberto={aberto}
        onAbrir={mudar}
        titulo={titulo}
        descricao={descricao}
        rodape={
          <>
            <Button type="button" variant="secundario" onClick={() => mudar(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="primario"
              onClick={() => void salvar()}
              disabled={!podeSalvar || salvando}
            >
              {salvando ? <Spinner /> : rotuloSalvar}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">{children}</div>
      </Sheet>
    </div>
  );
}
